import { z } from 'zod'
import { ISO_DATE_REGEX } from '@/util/time'

/**
 * Validates and parses a timestamp.
 * - Accepts numbers (assumes milliseconds since epoch).
 * - Accepts strings (assumes ISO 8601 or similar formats).
 * - Adds a Zod custom issue for invalid timestamps.
 */
export const parseTimestamp = (val: unknown, ctx: z.RefinementCtx): number | null => {
  // preprocess filtering - only includes valid timestamps
  // 1. Check if the value is a number and validate that it's in milliseconds since epoch.
  // 2. If it's not a number, attempt to parse it as a Python datetime string and convert it to milliseconds.
  if (val === null || val === undefined) {
    return null
  }

  // If it's a number, assume it's already in milliseconds
  if (typeof val === 'number') {
    // Validate it's milliseconds since epoch
    if (val < 1000000000000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Provided timestamp (${val}) is not in milliseconds since epoch`,
      })
      return z.NEVER // Return a value that will fail the schema
    }
    return val
  }

  // If it's not a number, try to parse it (should be provided as a python datetime string)
  const timestamp = Date.parse(val as string)
  if (isNaN(timestamp)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Provided timestamp (${val}) failed to Date.parse()`,
    })
    return z.NEVER // Return a value that will fail the schema
  }

  return timestamp
}

/**
 * Validates and parses an ISO 8601 date string in YYYY-MM-DD format.
 * - Accepts only ISO 8601 date strings (YYYY-MM-DD)
 * - Returns the date string as-is for storage
 * - Adds a Zod custom issue for invalid dates
 */
export const parseISODate = (val: unknown, ctx: z.RefinementCtx): string | null => {
  if (val === null || val === undefined) {
    return null
  }

  // Only accept string format
  if (typeof val === 'string') {
    // Validate ISO 8601 date format (YYYY-MM-DD)
    if (!ISO_DATE_REGEX.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Provided date (${val}) is not in ISO 8601 format (YYYY-MM-DD)`,
      })
      return z.NEVER
    }

    // Validate that it's a valid date
    const date = new Date(val + 'T00:00:00Z')
    if (isNaN(date.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Provided date (${val}) is not a valid date`,
      })
      return z.NEVER
    }

    return val
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Provided date must be a string in ISO 8601 format (YYYY-MM-DD), received ${typeof val}`,
  })
  return z.NEVER
}
